import { useMemo, useState } from 'react';
import {
  P2GAccordion,
  P2GBox,
  P2GButton,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GSearchSuggest,
  P2GText,
  SvgChevronRight
} from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { ProductApiModel } from '~/lib/api/smartsend/types';
import { AppCard } from '~/components/card/app-card';
import { SettingsCache } from '~/lib/consts/settings-cache';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { TranslationNamespaces } from '~/lib/constants';
import SidePanelSection, { SidePanelSections } from '~/components/sidepanel-section';
import { ProductSelector } from '~/apps/smartsend/components/product/ProductSelector';
import { buildQuickSendSteps } from './QuickSendSteps';

interface QuickSendEntryReturningProps {
  products: ProductApiModel[];
  onCreateShipment: (product?: ProductApiModel) => void;
  onCsvUpload: () => void;
  onRestore: () => void;
}

const formatDimensions = (p: ProductApiModel) => {
  const parts: string[] = [];
  if (p.Dimensions?.Weight) parts.push(`${p.Dimensions.Weight}kg`);
  if (p.Dimensions?.Length && p.Dimensions?.Width && p.Dimensions?.Height) {
    parts.push(`${p.Dimensions.Length} x ${p.Dimensions.Width} x ${p.Dimensions.Height} cm`);
  }
  return parts.join(' • ');
};

const QuickSendEntryReturning = ({
  products,
  onCreateShipment,
  onCsvUpload,
  onRestore
}: QuickSendEntryReturningProps) => {
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [search, setSearch] = useState('');
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const steps = buildQuickSendSteps(t);

  const hasCsvUpload = SettingsCache.settings.Features.includes('CsvUpload');
  const hasRestoreItem = SettingsCache.settings.Features.includes('RestoreItem');
  const cardCount = 1 + (hasCsvUpload ? 1 : 0) + (hasRestoreItem ? 1 : 0);
  const cardSize = 12 / cardCount;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => p.Description?.toLowerCase().includes(q) || p.Sku?.toLowerCase().includes(q));
  }, [search, products]);

  const viewProductsTitle =
    products.length > 1
      ? t('QUICK_SEND_VIEW_ALL_PRODUCTS', { defaultValue: 'View all {{count}} products', count: products.length })
      : t('QUICK_SEND_VIEW_SAVED_PRODUCT', { defaultValue: 'View saved product' });

  return (
    <SidePanelSections>
      <P2GText component="p" variant="body2">
        {t('QUICK_SEND_ENTRY_RETURNING_SUBHEADING', { defaultValue: 'Select a product or start a new shipment' })}
      </P2GText>

      <SidePanelSection>
        <P2GSearchSuggest<ProductApiModel>
          options={filtered}
          getOptionLabel={(p) => p.Description ?? ''}
          result={(p) => (
            <P2GBox>
              <P2GText variant="body2">
                <strong>{p.Description ?? '-'}</strong>
              </P2GText>
              {formatDimensions(p) && (
                <P2GText component="p" variant="caption">
                  {formatDimensions(p)}
                </P2GText>
              )}
            </P2GBox>
          )}
          onUpdate={setSearch}
          onSelect={(p) => onCreateShipment(p)}
          inputParams={{
            id: 'quick-send-search',
            label: t('QUICK_SEND_SEARCH_PRODUCTS_LABEL', { defaultValue: 'Search products' }),
            value: search
          }}
        />

        {search && filtered.length === 0 && (
          <P2GText component="p" variant="body2">
            {t('QUICK_SEND_NO_PRODUCTS_MATCH', { defaultValue: 'No products match your search.' })}
          </P2GText>
        )}
      </SidePanelSection>

      <P2GText component="p" variant="body2" align="center">
        {t('QUICK_SEND_OR_START_FRESH', { defaultValue: 'or start fresh' })}
      </P2GText>

      <P2GButton variant="primary" fullWidth onClick={() => onCreateShipment()}>
        {t('QUICK_SEND_CREATE_NEW_ORDER', { defaultValue: 'Create New Order' })}
      </P2GButton>

      <P2GGrid container spacing={2}>
        {hasCsvUpload && (
          <P2GGrid item size={{ xs: cardSize }}>
            <P2GBox onClick={onCsvUpload} sx={{ cursor: 'pointer', height: '100%' }}>
              <AppCard>
                <P2GBox p={2} sx={{ textAlign: 'center' }}>
                  <P2GText variant="body2">
                    <strong>{t('ADD_TOOLTIP_UPLOAD_CSV', { defaultValue: 'Upload CSV' })}</strong>
                  </P2GText>
                  <P2GText component="p" variant="caption" color="text.secondary">
                    {t('BULK_IMPORT_LABEL', { defaultValue: 'Bulk import' })}
                  </P2GText>
                </P2GBox>
              </AppCard>
            </P2GBox>
          </P2GGrid>
        )}
        <P2GGrid item size={{ xs: cardSize }}>
          <P2GBox onClick={() => goTo(RoutingConstants.AccountLinking)} sx={{ cursor: 'pointer', height: '100%' }}>
            <AppCard>
              <P2GBox p={2} sx={{ textAlign: 'center' }}>
                <P2GText variant="body2">
                  <strong>{t('CONNECT_STORE_LABEL', { defaultValue: 'Connect Store' })}</strong>
                </P2GText>
                <P2GText component="p" variant="caption" color="text.secondary">
                  {t('AUTO_SYNC_ORDERS_LABEL', { defaultValue: 'Auto-sync orders' })}
                </P2GText>
              </P2GBox>
            </AppCard>
          </P2GBox>
        </P2GGrid>
        {hasRestoreItem && (
          <P2GGrid item size={{ xs: cardSize }}>
            <P2GBox onClick={onRestore} sx={{ cursor: 'pointer', height: '100%' }}>
              <AppCard>
                <P2GBox p={2} sx={{ textAlign: 'center' }}>
                  <P2GText variant="body2">
                    <strong>{t('ADD_TOOLTIP_RESTORE_ITEM', { defaultValue: 'Restore Deleted' })}</strong>
                  </P2GText>
                  <P2GText component="p" variant="caption" color="text.secondary">
                    {t('RESTORE_ITEM_CAPTION', { defaultValue: 'Recover deleted orders' })}
                  </P2GText>
                </P2GBox>
              </AppCard>
            </P2GBox>
          </P2GGrid>
        )}
      </P2GGrid>

      <SidePanelSection>
        <P2GAccordion
          id="how-it-works-section"
          value="how-it-works"
          title={t('HOW_IT_WORKS_LABEL', { defaultValue: 'How it works' })}
          onChange={() => (_: React.SyntheticEvent, isExpanded: boolean) => setHowItWorksOpen(isExpanded)}
          expanded={howItWorksOpen}
        >
          <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {steps.map((step) => (
              <P2GGrid key={step.label} container spacing={1} alignItems="center" justify="flex-start" wrap="nowrap">
                <P2GGrid item>
                  <P2GIcon size="xs">{step.icon}</P2GIcon>
                </P2GGrid>
                <P2GGrid item>
                  <P2GText variant="body2">
                    <strong>{step.label}</strong>
                  </P2GText>
                  <P2GText component="p" variant="caption">
                    {step.detail}
                  </P2GText>
                </P2GGrid>
              </P2GGrid>
            ))}
          </P2GBox>
        </P2GAccordion>

        {products.length > 0 && (
          <P2GBox mt={2}>
            <ProductSelector products={() => Promise.resolve(products)} onSelect={onCreateShipment}>
              <P2GLink underline="none">
                <P2GBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <P2GText variant="body2">{viewProductsTitle}</P2GText>
                  <P2GIcon size="xs" children={<SvgChevronRight />} />
                </P2GBox>
              </P2GLink>
            </ProductSelector>
          </P2GBox>
        )}
      </SidePanelSection>
    </SidePanelSections>
  );
};

export default QuickSendEntryReturning;
